import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AUTH } from '@/constants/testIds';
import { useAuth } from '@/components/AuthProvider';
import { AuthShell } from '@/components/AuthShell';
import { api, setToken } from '@/lib/api';
import * as Icons from 'lucide-react';
import {
  firebaseEmailLogin,
  firebaseForgotPassword,
  firebaseSendVerificationEmail,
  isFirebaseClientConfigured,
  mapFirebaseAuthError,
} from '@/lib/firebaseClient';

export default function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const firebaseReady = isFirebaseClientConfigured();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!firebaseReady) {
      setError('Firebase is not configured on this build.');
      return;
    }
    setLoading(true);
    setError('');
    setInfo('');
    try {
      const idToken = await firebaseEmailLogin(email.trim(), password);
      setToken(idToken);
      const session = await api('/api/v1/auth/session', {
        method: 'POST',
        token: idToken,
        body: {},
      });
      if (session.status !== 200) {
        setError(
          String(
            session.data?.error ||
              session.data?.hint ||
              'Login failed — this account needs company access. Contact your admin.'
          )
        );
        setToken(null);
        return;
      }
      const profile = await refresh();
      const role = profile?.user?.role ?? session.data?.user?.role;
      const verified =
        profile?.user?.emailVerified ?? session.data?.user?.emailVerified ?? true;

      if (role !== 'super_admin' && verified === false) {
        setInfo('Signed in. Verify your email to unlock the control room.');
        navigate('/', { replace: true });
        return;
      }
      navigate(role === 'super_admin' ? '/super' : '/', { replace: true });
    } catch (err) {
      setError(mapFirebaseAuthError(err));
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    try {
      await firebaseForgotPassword(email.trim());
      setInfo('If an account exists for that email, a reset link has been sent.');
      setMode('login');
    } catch (err) {
      setError(mapFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setInfo('');
    try {
      if (!getTokenSafe()) {
        if (!email.trim() || !password) {
          setError('Enter email and password, then try Resend verification.');
          return;
        }
        const idToken = await firebaseEmailLogin(email.trim(), password);
        setToken(idToken);
      }
      await firebaseSendVerificationEmail();
      setInfo('Verification email sent.');
    } catch (err) {
      setError(mapFirebaseAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  function getTokenSafe() {
    try {
      return localStorage.getItem('ls_token');
    } catch {
      return null;
    }
  }

  return (
    <AuthShell
      eyebrow="Company sign-in"
      headline="Open your freight control room."
      subhead="Dispatchers, accountants, and admins sign in with a verified work email."
    >
      <div className="space-y-5">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            {mode === 'login' ? 'Welcome back' : 'Reset password'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {mode === 'login'
              ? firebaseReady
                ? 'Sign in to dispatch, settlements, and company settings.'
                : 'Firebase is not configured on this build.'
              : 'We will email a secure reset link to your work inbox.'}
          </p>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {info ? <p className="text-sm text-primary">{info}</p> : null}

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="you@carrier.com"
                data-testid={AUTH.loginEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  data-testid={AUTH.loginPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <Icons.EyeOff className="h-4 w-4" />
                  ) : (
                    <Icons.Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full"
              data-testid={AUTH.loginSubmit}
              disabled={loading || !firebaseReady}
            >
              {loading ? 'Signing in…' : 'Enter control room'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleForgot} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Work email</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || !firebaseReady}>
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        )}

        <div className="flex flex-col gap-2 text-center text-sm">
          {mode === 'login' ? (
            <>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  setMode('forgot');
                  setError('');
                  setInfo('');
                }}
              >
                Forgot password?
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:underline"
                onClick={handleResend}
                disabled={loading}
              >
                Resend verification email
              </button>
            </>
          ) : (
            <button type="button" className="text-primary hover:underline" onClick={() => setMode('login')}>
              Back to sign in
            </button>
          )}
        </div>

        <div className="border-t border-border/80 pt-5 space-y-2 text-center text-sm text-muted-foreground">
          <p>
            New carrier company?{' '}
            <a
              href="https://logisticslash.com/founding"
              className="text-primary hover:underline font-medium"
              target="_blank"
              rel="noreferrer"
            >
              Join founding
            </a>
          </p>
          <p>
            Have an invite or founding checkout?{' '}
            <Link to="/signup" className="text-primary hover:underline">
              Finish setup
            </Link>
          </p>
        </div>
    </div>
    </AuthShell>
  );
}
